import type { Schemas } from '@argzombie/api-client';
import { gearInUse, kgLabel, packSummary, restLabel, type InUseRow } from '../lib/playerSupport';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1 align-top';

function Member({ m }: { m: Schemas['TeamMemberView'] }) {
  return (
    <li>
      {m.isPlayer ? <strong>{m.name} (player)</strong> : m.name}
      {m.roleId ? ` · ${m.roleId}` : ''}
      {m.traitId ? ` · ${m.traitId}` : ''}{' '}
      {m.rest ? <span className="font-mono">{restLabel(m.rest)}</span> : <span className="font-mono">{m.health} HP</span>}
    </li>
  );
}

/** One line per holder ("Mara: M9, Riot vest (armor)"), or "none". */
function GearLines({ lines }: { lines: string[] }) {
  if (lines.length === 0) return <span className="text-bruise">none</span>;
  return (
    <ul className="text-xs">
      {lines.map((l) => (
        <li key={l}>{l}</li>
      ))}
    </ul>
  );
}

/**
 * A player's teams (the player detail's `teams`, the same `TeamsResponse` as `GET /teams`). Since the shared goods pool (ADR 0055)
 * a team's pack is its raw materials until a check-in (plus the raid kit while it raids, `kitTeamId`), and gear is a set of
 * references into the pool: "Gear in use" lists who wears what (`inUse`, the inventory's slot references).
 */
export function TeamsPanel({
  teams,
  inUse,
  kitTeamId,
  itemName,
}: {
  teams: Schemas['TeamsResponse'] | null | undefined;
  inUse?: InUseRow[] | null;
  kitTeamId?: string | null;
  itemName?: (id: string) => string;
}) {
  if (!teams) return <p className="text-sm">No run, so no teams.</p>;
  const benchGear = gearInUse(inUse, null, itemName);
  return (
    <>
      <p className="text-sm">
        Slots {teams.slotsUnlocked}/{teams.maxTeams} · bought-energy reserve{' '}
        <span className="font-mono">{teams.energyReserve}</span>
        {teams.convoyActive ? ' · in a convoy' : ''}
        {teams.playerOnBench ? ' · player on the bench' : ''}
      </p>
      {teams.teams.length === 0 ? (
        <p className="mt-2 text-sm text-bruise">No teams.</p>
      ) : (
        <table className="mt-2 w-full border-2 border-ink text-sm">
          <thead className="bg-ink text-newsprint">
            <tr>
              <th className={th}>Slot</th>
              <th className={th}>Status</th>
              <th className={th}>Energy</th>
              <th className={th}>Members</th>
              <th className={th}>Gear in use</th>
              <th className={th}>Materials</th>
            </tr>
          </thead>
          <tbody>
            {teams.teams.map((t) => (
              <tr key={t.id} className="border-t border-newsprint">
                <td className={`${td} font-mono`}>{t.slot}</td>
                <td className={td}>
                  {t.status}
                  {t.buildingId != null && <div className="font-mono text-xs">#{t.buildingId}</div>}
                  {t.move && t.status === 'travelling' && (
                    <div className="text-xs">
                      arrives {new Date(t.move.arriveAt).toLocaleString()}
                    </div>
                  )}
                </td>
                <td className={td}>
                  <span className="font-mono">
                    {t.energy.toFixed(1)}/{t.energyCap}
                  </span>{' '}
                  {t.tier}
                  <div className="font-mono text-xs">+{t.regenPerHour.toFixed(1)}/h</div>
                </td>
                <td className={td}>
                  <ul>
                    {t.members.map((m) => (
                      <Member key={m.survivorId ?? 'player'} m={m} />
                    ))}
                  </ul>
                </td>
                <td className={td}>
                  <GearLines lines={gearInUse(inUse, t.id, itemName)} />
                </td>
                <td className={td}>
                  <span className="font-mono">{kgLabel(t.pack.kg, t.pack.capacityKg)}</span>
                  {kitTeamId === t.id && (
                    <span className="ml-1 rounded bg-caption px-1 text-xs font-semibold">
                      + raid kit
                    </span>
                  )}
                  <div className="font-mono text-xs">{packSummary(t.pack)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {teams.bench.length > 0 && (
        <p className="mt-2 text-sm">
          Bench:{' '}
          {teams.bench
            .map((s) => `${s.name} (${s.roleId}, ${s.rest ? restLabel(s.rest) : `${s.health} HP`})`)
            .join(', ')}
        </p>
      )}
      {benchGear.length > 0 && <p className="text-sm">Bench gear: {benchGear.join(' · ')}</p>}
      <p className="mt-1 text-xs text-bruise">
        Goods nobody wears are in the shared pool (Inventory, base). Materials ride with their team
        until a check-in at home or a camp.
      </p>
    </>
  );
}
